import Button from "@mui/material/Button"
import Card from "@mui/material/Card"
import CardContent from "@mui/material/CardContent"
import Chip from "@mui/material/Chip"
import Divider from "@mui/material/Divider"
import Stack from "@mui/material/Stack"
import Typography from "@mui/material/Typography"

import {
  MOBILE_RELEASE_NOTE_LOCALES,
  type MobileAppVersionConfig,
  type MobileUpdateType
} from "@models/mobile_app_version"

const CHIP_COLOR: Record<MobileUpdateType, "default" | "info" | "error"> = {
  NONE: "default",
  OPTIONAL: "info",
  FORCED: "error"
}

export default function PlatformConfigCard({
  config,
  translate,
  canEdit,
  onEdit
}: {
  config: MobileAppVersionConfig
  translate: (key: string) => string
  canEdit: boolean
  onEdit: () => void
}) {
  const updateType = config.updateType ?? "NONE"
  const platformLabel = config.platform === "IOS" ? "iOS" : "Android"

  return (
    <Card variant="outlined" sx={{ flex: 1, minWidth: "18rem" }}>
      <CardContent>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 1.5 }}
        >
          <Typography variant="h6">{platformLabel}</Typography>
          <Chip
            size="small"
            color={CHIP_COLOR[updateType]}
            label={translate(
              `mobile_app_version_type_${updateType.toLowerCase()}`
            )}
          />
        </Stack>

        <Divider sx={{ mb: 1.5 }} />

        <Stack gap={0.75}>
          <Typography variant="body2">
            {translate("mobile_app_version_latest")}:{" "}
            <strong>{config.latestVersion || "—"}</strong>
          </Typography>
          <Typography variant="body2">
            {translate("mobile_app_version_min_supported")}:{" "}
            <strong>{config.minSupportedVersion || "—"}</strong>
          </Typography>
          <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
            {translate("mobile_app_version_store_url")}:{" "}
            <strong>{config.storeUrl || "—"}</strong>
          </Typography>

          <Stack direction="row" gap={0.5} alignItems="center" sx={{ mt: 0.5 }}>
            <Typography variant="body2">
              {translate("mobile_app_version_release_notes")}:
            </Typography>
            {MOBILE_RELEASE_NOTE_LOCALES.map((locale) => (
              <Chip
                key={locale}
                size="small"
                variant={
                  config.releaseNotes?.[locale]?.body?.trim()
                    ? "filled"
                    : "outlined"
                }
                color={
                  config.releaseNotes?.[locale]?.body?.trim()
                    ? "success"
                    : "default"
                }
                label={locale.toUpperCase()}
              />
            ))}
          </Stack>

          {!!config.updatedAt && (
            <Typography variant="caption" color="text.secondary">
              {translate("mobile_app_version_updated_at")}:{" "}
              {new Date(config.updatedAt).toLocaleString()}
              {config.updatedBy ? ` · ${config.updatedBy}` : ""}
            </Typography>
          )}
        </Stack>

        {canEdit && (
          <Button
            variant="outlined"
            size="small"
            onClick={onEdit}
            sx={{ mt: 2 }}
            fullWidth
          >
            {translate("mobile_app_version_edit")}
          </Button>
        )}
      </CardContent>
    </Card>
  )
}
